import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import AdyenCreditCardForm, {
  AdyenCreditCardFormRef,
} from '@components/AdyenCreditCardForm/AdyenCreditCardForm';
import DeleteAllCardsModal from '@components/Overlays/Modals/DeleteAllCardsModal/DeleteAllCardsModal';
import DeleteCardModal from '@components/Overlays/Modals/DeleteCardModal/DeleteCardModal';
import StoredCreditCards from '@components/StoredCreditCards/StoredCreditCards';
import NewAdyenCreditCardModal from '@features/AdyenCreditCard/NewAdyenCreditCardModal/NewAdyenCreditCardModal';
import useEmergencyState from '@hooks/useEmergencyState';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import CoreProviders, { CoreProvidersProps } from '@providers/CoreProviders';
import CreditCardsMutationProvider, {
  useCreditcardService,
} from '@providers/CreditCardsMutationProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { captureException } from '@services/sentryService';
import { AdyenEnvironment } from '@types-internal/AdyenTypes';
import { CreditCardBrand, CreditCardData, CustomerType } from '@types-internal/index';
import { exposeGlobally } from '@utils/utils';

import styles from './AdyenManageCreditCards.module.scss';

export interface AdyenManageCreditCardsProps extends CoreProvidersProps {
  containerType: 'myaccount' | 'default';
  customerType: CustomerType;
  currency: string;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
  supportedCreditCardBrands: CreditCardBrand[];
  creditCards: CreditCardData[];
  isRouteToAdyenDueToMarketplace?: boolean;
  isCartWithMarketplaceItems?: boolean;
  customerHasCreditCardAutoshipment?: boolean;
}

/*
 * On "My Account", we display the credit cards (if any, check done in StoredCreditCards).
 * And we provide ways to delete them, either individually or all at once.
 */
const AdyenManageCreditCards_MyAccount: React.FC<{ isFallbackPsp: boolean }> = ({
  isFallbackPsp,
}) => {
  return (
    <>
      <StoredCreditCards isMyAccount={true} isFallbackPsp={isFallbackPsp} />
      <DeleteAllCardsModal />
      <DeleteCardModal />
    </>
  );
};

/*
 * During checkout, when the customer has no credit cards, we display directly the form to add a new credit card.
 * This is the only action that the customer can do and that's why it does not make sense to use the NewAdyenCreditCardModal.
 *
 * We need to expose globally the 'saveAdyenCreditCard' since the submit is outside the form.
 */
const AdyenManageCreditCards_CheckoutNoCard: React.FC<AdyenManageCreditCardsProps> = ({
  adyenEnvironment,
  supportedCreditCardBrands,
  customerType,
  isFallbackPsp,
  isRouteToAdyenDueToMarketplace,
  isCartWithMarketplaceItems,
  customerHasCreditCardAutoshipment,
}) => {
  const { t } = useTranslation();
  const { config } = useConfig();
  const form = useRef<AdyenCreditCardFormRef>(null);
  const { setError } = useErrorContext();
  const creditcardService = useCreditcardService();
  const [showForm, setShowForm] = useState(true);

  const saveAdyenCreditCard = useCallback(
    async function (): Promise<boolean> {
      if (form.current === null) {
        setError({ message: t('checkout.payment.creditcard.error.addcard.apicall') });
        return false;
      } else {
        const adyenFormData = form.current.computeAdyenCreditCardFormData();

        if (adyenFormData === null) {
          return false;
        } else {
          return creditcardService
            .saveAdyenCreditCard(adyenFormData)
            .then((isSuccess) => {
              if (isSuccess) {
                AdobeTrackingService.adyenTracking('AdyenFormOK');
                if (config.env !== 'local') {
                  window.location.assign('/checkout/preview.htm');
                }
              } else {
                AdobeTrackingService.adyenTracking('AdyenFormKO');
                setError({ message: t('checkout.payment.creditcard.error.addcard.apicall') });
              }

              return isSuccess;
            })
            .catch((error: unknown) => {
              AdobeTrackingService.adyenTracking('AdyenFormKO');
              // on any unexpected error, send it to Sentry and display a generic error message to the user
              captureException({ error });
              setError({ message: t('checkout.payment.creditcard.error.addcard.apicall') });
              return false;
            });
        }
      }
    },
    [form],
  );
  useEffect(
    () => exposeGlobally('saveAdyenCreditCard', saveAdyenCreditCard),
    [saveAdyenCreditCard],
  );

  const onFatalError = useCallback(
    (error: unknown, showForm?: boolean) => {
      captureException({ error });
      setError({
        message: t('checkout.payment.creditcard.error.addcard.apicall'),
      });
      setShowForm(typeof showForm === 'undefined' ? true : showForm);
    },
    [setError],
  );

  return (
    <>
      {isRouteToAdyenDueToMarketplace ? (
        <Alert className={styles.adyenFormAlert} variant="caution">
          {t('checkout.payment.routed.adyen.marketplace.message')}
        </Alert>
      ) : (
        <Alert className={styles.adyenFormAlert}>
          <Trans
            t={t}
            i18nKey="checkout.payment.creditcard.3d.secure.message"
            components={{ url: <a target="_blank" href="/info/about/psd2_summary" /> }}
          />
        </Alert>
      )}
      {showForm && (
        <AdyenCreditCardForm
          ref={form}
          adyenEnvironment={adyenEnvironment}
          clientKey={config.adyen.clientKey}
          supportedCreditCardBrands={supportedCreditCardBrands}
          onFatalError={onFatalError}
          allowVaulting={!isFallbackPsp && customerType !== 'GUEST_CUSTOMER'}
          isCartWithMarketplaceItems={isCartWithMarketplaceItems}
          isRoutedToAdyenDueToMarketplace={isRouteToAdyenDueToMarketplace}
          customerHasCreditCardAutoshipment={customerHasCreditCardAutoshipment}
          labelInline={true}
        />
      )}
    </>
  );
};

/*
 * During checkout, when the customer has some credit cards, we display them and we provide ways to add a new one in a modal, and to delete the existing credit cards, either individually or all at once.
 */
const AdyenManageCreditCards_CheckoutWithCards: React.FC<AdyenManageCreditCardsProps> = ({
  adyenEnvironment,
  supportedCreditCardBrands,
  customerType,
  isFallbackPsp,
}) => {
  return (
    <>
      <StoredCreditCards isMyAccount={false} isFallbackPsp={isFallbackPsp} />
      <NewAdyenCreditCardModal
        adyenEnvironment={adyenEnvironment}
        supportedCreditCardBrands={supportedCreditCardBrands}
        customerType={customerType}
        isFallbackPsp={isFallbackPsp}
      />
      <DeleteAllCardsModal />
      <DeleteCardModal />
    </>
  );
};

/**
 * Dispatch to the proper UI to use depending on whether the customer has some credit cards or not.
 */
const AdyenManageCreditCards_Checkout: React.FC<AdyenManageCreditCardsProps> = (props) => {
  const {
    data: { creditCards },
  } = useCreditcardService();

  return (
    <>
      {creditCards.length > 0 ? (
        <AdyenManageCreditCards_CheckoutWithCards {...props} />
      ) : (
        <AdyenManageCreditCards_CheckoutNoCard {...props} />
      )}
    </>
  );
};

const AdyenManageCreditCardsSwitch: React.FC<AdyenManageCreditCardsProps> = (props) => {
  const { t } = useTranslation();
  const { setError } = useErrorContext();

  const { containerType, isFallbackPsp } = props;
  const isMyAccount = containerType === 'myaccount';

  useEmergencyState(isFallbackPsp, isMyAccount, t, setError);

  return isMyAccount ? (
    <AdyenManageCreditCards_MyAccount isFallbackPsp={isFallbackPsp} />
  ) : (
    <AdyenManageCreditCards_Checkout {...props} />
  );
};

/**
 * Main component which is covering all the possible UI on the 'editpayment' pages (My Account & Checkout funnel).
 */
const AdyenManageCreditCards: React.FC<AdyenManageCreditCardsProps> = (props) => {
  const { env, shopBundle, locale, domain, errorContainerId, csrfToken, creditCards } = props;

  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      csrfToken={csrfToken}
      featureName="adyen-credit-card">
      <CreditCardsMutationProvider
        pspToken={''}
        creditCards={creditCards}
        psp="ADYEN"
        paymentMethod="CREDIT_CARD">
        <AdyenManageCreditCardsSwitch {...props} />
      </CreditCardsMutationProvider>
    </CoreProviders>
  );
};

export default AdyenManageCreditCards;
